import { PharmaStpFileApiResponse } from "../../../api/bigQuery/pharmaStpFileOrders";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { logError } from "../../../utils/customLogger";
import { NycbsExternalFilterPayload } from "../hooks/useFilterPayload";
import { NycbsExternalTableState } from "../types/nycbsExternalTableState";
import { fetchNycbsExternalData } from "./fetchNycbsExternalData";

/**
 * makePostCallForNextPagesDataFetch - API Call for Pagination
 *
 * Purpose: Fetches next page of data when user scrolls to the end.
 * Appends new data to existing tableData without resetting.
 *
 * @param payload - Filter payload containing search params with incremented page number
 * @param setNycbsExternalState - State setter for API response
 * @param setTableData - State setter for table data (appends to existing data)
 */
export const makePostCallForNextPagesDataFetch = async (
  payload: NycbsExternalFilterPayload,
  setNycbsExternalState: React.Dispatch<
    React.SetStateAction<NycbsExternalTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<PharmaStpFileModel[]>>,
): Promise<void> => {
  try {
    setNycbsExternalState((prev) => ({
      ...prev,
      showInlineLoader: true,
      error: null,
    }));

    const response: PharmaStpFileApiResponse =
      await fetchNycbsExternalData(payload);

    if (response.success) {
      setNycbsExternalState({
        data: response,
        loading: false,
        error: null,
        showInlineLoader: false,
      });

      // Append new data to existing data
      setTableData((prevData) => [...prevData, ...response.rows]);
    } else {
      setNycbsExternalState((prev) => ({
        ...prev,
        showInlineLoader: false,
        error: response.message || "Failed to fetch next page",
      }));
    }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "An unknown error occurred";
    setNycbsExternalState((prev) => ({
      ...prev,
      showInlineLoader: false,
      error: errorMessage,
    }));
    logError(error as Error, "Error in makePostCallForNextPagesDataFetch");
  }
};
